import { useEffect } from 'react';
import { dataManager } from '../utils/dataManager';
import { recalculateStats } from '../utils/statsEngine';

export function useStatsMigrationEffect(gameMode: string) {
  // Self-healing: Check for uncalculated stats on mount or mode change
  useEffect(() => {
    let retryTimeout: NodeJS.Timeout | null = null;
    let hasRun = false; // Prevent multiple executions

    const initializeStats = () => {
      // Prevent multiple executions if already completed
      if (hasRun) return;

      // CRITICAL: Wait for initial Firestore sync to complete before running stats calculation
      // This prevents a race condition where recalculateStats runs, updates state, but then
      // Firestore sync completes and overwrites the calculated data with cloud data
      const isInitialSyncComplete = dataManager.getIsInitialSyncComplete();

      if (!isInitialSyncComplete) {
        // Schedule retry after a short delay to wait for Firestore sync
        retryTimeout = setTimeout(() => {
          initializeStats();
        }, 500);
        return;
      }

      // Mark as run to prevent duplicate execution
      hasRun = true;

      const players = dataManager.getPlayers();
      const tournaments = dataManager.getTournaments();
      const matches = dataManager.getAllMatches();

      // Calculate total matches tracked in stats vs matches existing in history
      const totalStatsMatches = players.reduce((acc, p) => acc + p.stats.matchesPlayed, 0);

      // Count completed matches in tournaments (approximate check for activity)
      const totalHistoricalMatches = tournaments.reduce((acc, t) => {
        return acc + t.matches.filter(m => (m.s1 > 0 || m.s2 > 0 || m.isWalkover)).length;
      }, 0);

      // Check for missing snapshots (Migration trigger)
      // If we find any completed match without p1EloAfter, we must recalculate to backfill.
      // Exclude matches that are Walkovers if they don't have scores (though statsEngine handles them)
      const needsMigration = matches.some(m => {
        const isPlayed = (m.s1 > 0 || m.s2 > 0) || m.isWalkover;
        const isMissingSnapshot = m.p1EloAfter === undefined;
        return isPlayed && isMissingSnapshot;
      });

      // If we have history but 0 stats (or very low compared to history), hydrate
      if ((totalHistoricalMatches > 0 && totalStatsMatches === 0) || needsMigration) {
        console.log(`KFPPC [${gameMode}]: Detected uncalculated stats or missing snapshots. Running initialization...`);
        recalculateStats();
        // Force save to Firestore immediately to prevent data loss
        dataManager.forceSave();
      }
    };

    initializeStats();

    // Cleanup function to cancel pending retries
    return () => {
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [gameMode]);
}